import React, { useState, useEffect, useRef } from 'react';
import './index.scss';
import Button from '../../Button';
import ProgressBar from '../../ProgressBar';

export default function Stopwatch() {
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [records, setRecords] = useState([]);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isRunning && !isPaused) {
      intervalRef.current = setInterval(() => {
        setTime(prevTime => prevTime + 10);
      }, 10);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isRunning, isPaused]);

  // 键盘快捷键处理
  useEffect(() => {
    const handleKeyDown = (event) => {
      // 防止在输入框中触发快捷键
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
      }

      switch (event.code) {
        case 'Space':
          event.preventDefault();
          handleStartPause();
          break;
        case 'Delete':
        case 'Backspace':
          event.preventDefault();
          handleReset();
          break;
        case 'KeyE':
          event.preventDefault();
          handleRecord();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isRunning, isPaused, time]);

  // 开始/暂停计时
  const handleStartPause = () => {
    if (!isRunning) {
      // 开始计时
      setIsRunning(true);
      setIsPaused(false);
    } else {
      // 暂停计时
      setIsPaused(!isPaused);
    }
  };

  // 重置计时器
  const handleReset = () => {
    if (isPaused) {
      setTime(0);
      setRecords([]);
      setIsRunning(false);
      setIsPaused(false);
    }
  };

  // 记录时间
  const handleRecord = () => {
    if (isRunning) {
      const newRecord = {
        id: Date.now(),
        time: time,
        timestamp: new Date().toLocaleTimeString()
      };
      setRecords(prev => [...prev, newRecord]);
    }
  };

  const formatTime = (timeInMs) => {
    const minutes = Math.floor(timeInMs / 60000);
    const seconds = Math.floor((timeInMs % 60000) / 1000);
    const milliseconds = Math.floor((timeInMs % 1000) / 10);
    
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="stopwatch_container">
      <style>
        {`
          .stopwatch_scroll::-webkit-scrollbar {
            width: 8px;
          }
          .stopwatch_scroll::-webkit-scrollbar-track {
            background: #001100;
            border-radius: 4px;
          }
          .stopwatch_scroll::-webkit-scrollbar-thumb {
            background: #00ff00;
            border-radius: 4px;
            border: 1px solid #003300;
          }
          .stopwatch_scroll::-webkit-scrollbar-thumb:hover {
            background: #00cc00;
          }
        `}
      </style>
      
      {/* 快捷键提示 */}
      <div className="stopwatch_shortcuts">
        <div className="stopwatch_shortcut_item">
          <span className="stopwatch_shortcut_key">空格键</span>
          <span className="stopwatch_shortcut_desc">开始/暂停</span>
        </div>
        <div className="stopwatch_shortcut_item">
          <span className="stopwatch_shortcut_key">删除键</span>
          <span className="stopwatch_shortcut_desc">重置</span>
        </div>
        <div className="stopwatch_shortcut_item">
          <span className="stopwatch_shortcut_key">E键</span>
          <span className="stopwatch_shortcut_desc">记录</span>
        </div>
      </div>

      {/* 计时器显示 */}
      <div className="stopwatch_timer">
        {formatTime(time)}
      </div>
      
      {/* 控制按钮 */}
      <div className="stopwatch_controls">
        <Button
          onClick={handleStartPause}
          type={isRunning ? (isPaused ? 'primary' : 'warning') : 'primary'}
          size="large"
          className="stopwatch_btn stopwatch_btn_start"
        >
          {!isRunning ? '开始计时' : (isPaused ? '继续' : '暂停')}
        </Button>
        <Button
          onClick={handleReset}
          disabled={!isPaused}
          type="danger"
          size="large"
          className={`stopwatch_btn stopwatch_btn_reset${!isPaused ? ' stopwatch_btn_disabled' : ''}`}
        >
          重置
        </Button>
        <Button
          onClick={handleRecord}
          disabled={!isRunning}
          type="info"
          size="large"
          className={`stopwatch_btn stopwatch_btn_record${!isRunning ? ' stopwatch_btn_disabled' : ''}`}
        >
          记录次数
        </Button>
      </div>
      
      {/* 记录列表 */}
      <div className="stopwatch_record_list">
        <div className="stopwatch_record_list_title">
          记录列表 ({records.length})
        </div>
        <div 
          className="stopwatch_record_list_scroll"
        >
          {records.length === 0 ? (
            <div className="stopwatch_record_empty">
              暂无记录
            </div>
          ) : (
            [...records].reverse().map((record, index) => (
              <div
                key={record.id}
                className="stopwatch_record_item"
              >
                <span className="stopwatch_record_index">#{records.length - index}</span>
                <span className="stopwatch_record_time">
                  {formatTime(record.time)}
                </span>
                <span className="stopwatch_record_timestamp">
                  {record.timestamp}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
} 